import { Box } from '@mui/material';
import { useContext } from 'react';
import { CircleBackground } from './CircleBackground';
import { DATA_TEST } from './enums';
import { ResultContext, sizeStyles } from './ResultContext';
import type { ResultIconProps } from './types';
import { getStatusIconAndColor } from './utils/status';

/**
 * Renders an icon inside a circular background, responsive to the Result size.
 * - Color is applied via the parent Box using the status from context (or 'empty' if not set).
 * Icons should use currentColor for best results.
 */
export const ResultIcon = ({ sx = {}, children, dataTest }: ResultIconProps) => {
  const context = useContext(ResultContext);
  if (!context) throw new Error('Result.Icon must be used within an Result component');
  const { size, isMobile, status } = context;
  const { iconSize, spacing } = sizeStyles(size, isMobile);
  const { color } = getStatusIconAndColor(status);

  // Calculate circle size and padding
  const iconPx = parseInt(iconSize, 10);
  const circleSize = iconPx * 2;

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', mb: spacing }}
      data-test={dataTest ?? DATA_TEST.result_icon_root}
    >
      <CircleBackground width={circleSize} height={circleSize}>
        <Box
          data-test={DATA_TEST.result_icon}
          sx={{
            width: iconSize,
            height: iconSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color, // This sets the color for all children using currentColor
            ...sx,
            '& svg': {
              width: '100%',
              height: '100%'
            }
          }}
        >
          {children}
        </Box>
      </CircleBackground>
    </Box>
  );
};
